import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Phone, Palette, MessageSquare, Rocket, CheckCircle, ArrowRight
} from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa';

const WHATSAPP_URL = 'https://wa.me/919663112394?text=Hi!%20I%27m%20interested%20in%20getting%20a%20website%20for%20my%20business';

const Journey = () => {
  const steps = [
    {
      num: '01',
      icon: Phone,
      title: 'Tell us about your business',
      desc: '5 min call or WhatsApp chat to understand your needs',
      color: 'bg-primary-500'
    },
    {
      num: '02',
      icon: Palette,
      title: 'We design & build',
      desc: 'Our team crafts your website in 24-48 hours',
      color: 'bg-secondary-500'
    },
    {
      num: '03',
      icon: MessageSquare,
      title: 'You review & request changes',
      desc: 'Unlimited revisions until you love it',
      color: 'bg-accent-500'
    },
    {
      num: '04',
      icon: Rocket,
      title: 'Go live on your domain',
      desc: 'Same day deployment. Start getting customers.',
      color: 'bg-primary-600'
    }
  ];

  const plans = [
    {
      name: 'Starter',
      price: '9,999',
      desc: 'Perfect for new businesses',
      features: [
        '5-page website',
        'Booking system',
        'Mobile responsive',
        'WhatsApp integration',
        'Free domain (1 year)',
        'Basic SEO',
        'Email support'
      ],
      popular: false,
      cta: 'Get Started'
    },
    {
      name: 'Professional',
      price: '14,999',
      desc: 'For growing businesses',
      features: [
        'Everything in Starter',
        'Custom design',
        'AI chatbot',
        'Analytics dashboard',
        'Social media integration',
        'Priority support',
        'Free photoshoot'
      ],
      popular: true,
      cta: 'Most Popular'
    },
    {
      name: 'Enterprise',
      price: 'Custom',
      desc: 'Full-service solution',
      features: [
        'Everything in Professional',
        'Multi-location support',
        'Advanced integrations',
        'Dedicated account manager',
        'Custom features',
        'White-label option'
      ],
      popular: false,
      cta: 'Contact Us'
    }
  ];

  const included = [
    'Unlimited revisions',
    '48-hour delivery',
    'Free hosting & SSL',
    '24/7 uptime monitoring'
  ];

  return (
    <>
      {/* How It Works */}
      <section className="py-20 lg:py-28 bg-white" data-testid="how-it-works-section">
        <div className="container">
          <motion.div
            className="text-center mb-14"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="inline-block px-4 py-2 bg-accent-50 text-accent-600 font-medium rounded-full text-sm mb-4">
              Simple Process
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-neutral-900">
              How It Works
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, i) => (
              <motion.div
                key={step.num}
                className="relative text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15 }}
                data-testid={`step-${i}`}
              >
                {i < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-10 left-[60%] w-[80%] h-0.5 bg-gradient-to-r from-neutral-200 to-neutral-100" />
                )}
                <div className={`w-20 h-20 ${step.color} rounded-2xl flex items-center justify-center mx-auto mb-5 shadow-lg`}>
                  <step.icon className="w-9 h-9 text-white" />
                </div>
                <span className="text-xs font-mono font-bold text-primary-400 tracking-wider">STEP {step.num}</span>
                <h3 className="font-heading font-bold text-lg text-neutral-900 mt-2 mb-2">
                  {step.title}
                </h3>
                <p className="text-neutral-500 text-sm">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 lg:py-28 bg-section-alt" data-testid="pricing-section">
        <div className="container">
          <motion.div
            className="text-center mb-14"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="inline-block px-4 py-2 bg-primary-50 text-primary-600 font-medium rounded-full text-sm mb-4">
              Pricing
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-neutral-900 mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-neutral-500 max-w-xl mx-auto">
              No hidden fees. No surprises. Pick the plan that fits your business.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto mb-12">
            {plans.map((plan, i) => (
              <motion.div
                key={plan.name}
                className={`card-base ${plan.popular ? 'pricing-popular scale-[1.02]' : ''}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                data-testid={`pricing-card-${plan.name.toLowerCase()}`}
              >
                <h3 className="font-heading text-xl font-bold text-neutral-900 mb-1">{plan.name}</h3>
                <p className="text-neutral-500 text-sm mb-5">{plan.desc}</p>
                <div className="mb-6">
                  {plan.price === 'Custom' ? (
                    <span className="text-3xl font-heading font-bold text-neutral-900">Custom</span>
                  ) : (
                    <>
                      <span className="text-sm text-neutral-500">Rs </span>
                      <span className="text-4xl font-heading font-extrabold text-neutral-900">{plan.price}</span>
                      <span className="text-neutral-500">/month</span>
                    </>
                  )}
                </div>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5">
                      <CheckCircle className="w-5 h-5 text-accent-500 flex-shrink-0 mt-0.5" />
                      <span className="text-neutral-600 text-sm">{f}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/contact"
                  className={`w-full text-center block rounded-xl py-3 font-semibold transition-all ${
                    plan.popular
                      ? 'bg-primary-500 text-white hover:bg-primary-600 shadow-lg shadow-primary-500/25'
                      : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                  }`}
                  data-testid={`pricing-cta-${plan.name.toLowerCase()}`}
                >
                  {plan.cta}
                </Link>
              </motion.div>
            ))}
          </div>

          <motion.div
            className="flex flex-wrap justify-center gap-6 md:gap-10"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            {included.map((item) => (
              <div key={item} className="flex items-center gap-2 text-neutral-600">
                <CheckCircle className="w-5 h-5 text-accent-500" />
                <span className="font-medium text-sm">{item}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default Journey;
